import Backbone from 'backbone'
import _ from 'lodash'
import Nav from 'scoreboard/components/nav'
import Dataset from './model/dataset'
import NewTab from 'scoreboard/components/new-tab'
import DatasetsGardener from './datasets-gardener'
import i18n from 'scoreboard/components/i18n'
import conductor from 'scoreboard/components/conductor'
import HttpStatusCodes from 'http-status-codes'
import noViewableDatasetsNav from './no-viewable-datasets-nav.hbs'
import Tooltip from 'scoreboard/components/tooltip'
import guard from 'scoreboard/components/unsaved/guard'
import permissions from 'scoreboard/components/permissions'
import unwrapModel from 'scoreboard/components/unwrap-model'

var DatasetsNav = Nav.extend({

	section: 'datasets',
	Model: Dataset,
	Gardener: DatasetsGardener,
	folders: true,

	events: _.extend({}, Nav.prototype.events, NewTab.events, {
		'click .new': 'addDataset'
	}),

	// #13649 don't fiddle with the selection.  We are setting an
	// initialSelection in datasets-router.js:default instead
	_ensureSelection: _.identity,

	/**
	 * called by the display header, but the tree editability is controlled by
	 * _setEditability
	 */
	isEditable: function(orgId) {
		// If one of the datasets in this org has custom permissions we'll just
		// have to show the "You don't have permission to edit this dataset" view
		// when they navigate to it.
		return permissions.modifyAllDatasets(orgId) ||
			permissions.modifyStandardPermissionDatasets(orgId)
	},

	changeUrl: true,

	_bindEvents: function() {
		Nav.prototype._bindEvents.apply(this, arguments)
		// Dataset permissions are so complicated now that we can't use the tree
		// node attributes, and we must check each dataset/view/folder as it's loaded
		// triggered from base-router#applyModel after the dataset is returned
		Backbone.on('apply:domain-object', model => {
			if (model.domainType === 'dataset') {
				// _setEditability modifies the state of the 'New Dataset' and
				// 'New Folder' buttons, so make sure the tree has been rendered
				this.gardener._onceTreeRendered(() =>
					this._setEditability(model))
			}
		})
	},

	_toggleEditabilityIfNeeded() {
		// noop, overridden to prevent flicker
		// this nav listens to 'apply:domain-object' and handles editablity itself
	},

	_setEditability: function (model) {
		const editable = model.isEditable()
		this.toggleEditability(editable)
		// disables the 'new dataset' and 'new folder' buttons
		this._toggleButtonEditability(this.$('.nav-top-toolbar button'),
			editable)
		this._toggleTreeButtons(model)
	},

	/**
	 * Toggle the enabled state of the 'New Dataset' button
	 * If the tree node is editable, a folder can always be created.
	 * @param {Dataset} dataset
	 */
	_toggleTreeButtons: function(dataset) {
		const newDatasetButton = this.$('button.new')
		const isModifiable = dataset.isModifyDataset()
		this._toggleButtonEditability(newDatasetButton, isModifiable)

		if (isModifiable) {
			let addDatasetError = this._testCanAddDataset(dataset)
			this._toggleButtonEditability(newDatasetButton, _.isNil(addDatasetError))
		}
	},

	_toggleButtonEditability: function(el, editable) {
		el.toggleClass('is-disabled', !editable)
			.prop('disabled', !editable)
	},

	/**
	 * @inheritdoc
	 */
	onSelect: function(node) {
		// if we were on the records tab and then clicked on a folder
		if (node.type === 'folder' && _.endsWith(Backbone.history.fragment, 'records')) {
			// go to the overview tab
			conductor.overview('datasets', node.id)
		} else {
			Nav.prototype.onSelect.apply(this, arguments)
		}
	},

	// Show a tooltip warning on a given tree toolbar button
	_showTreeButtonWarning: function(attachTo, content) {
		_.invoke(this.toolbarTooltip, 'remove')
		this.toolbarTooltip = new Tooltip({
			attachTo,
			content,
			placement: 'below',
			arrow: 'center'
		}).render()
	},

	_emptyTree: function() {
		if (this.gardener.stream.status == HttpStatusCodes.FORBIDDEN) {
			this.$el.empty().append(noViewableDatasetsNav())
			this.clearLoadingDots()
		} else {
			Nav.prototype._emptyTree.apply(this, arguments)
			this.$('.empty-tree-message').text(i18n.get('datasets.missing.domainObject.1'))
		}
	},

	// Given a relative node (direct parent), determine whether a dataset can be
	// added under that node.
	_testCanAddDataset(dataset) {
		const relative = unwrapModel(dataset)
		if (_.isNil(relative)) {
			return
		}

		// Make sure we're not trying to add a dataset as a child of another dataset
		if (relative.type == 'view' ||
			relative.rootDatasetId && relative.rootDatasetId != relative.id) {
			return 'datasets.noDatasetUnderDataset'
		}
	},

	addDataset: function() {
		this._onceTreeLoaded(() => {
			let relativeId = _.get(_.result(this, 'gardener.tree.selected'), 'id')
			let addDatasetError = this._testCanAddDataset(this.gardener.tree.get(relativeId))
			if (addDatasetError) {
				this._showTreeButtonWarning(this.$('button.new'), i18n.get(addDatasetError))
				return
			}

			// At this point, both rules were satisfied, we can now go on to
			// creating the new dataset.
			guard.conditionallyRun(() => {
				// #44888 instead of opening the datasets wizard, navigate to the
				// "New Dataset" page that displays the data source options
				conductor.navigate('datasets/' + relativeId + '/new', {trigger: true})
			})
		})
	},

	remove: function() {
		Backbone.View.prototype.remove.apply(this, arguments)
		_.invokeMap([this.toolbarTooltip], 'remove')
	}

})

_.extend(DatasetsNav.prototype, _.omit(NewTab, ['events']))

export default DatasetsNav

DatasetsNav.__filename__ = 'datasets/datasets-nav.js'
